import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ExternalLink, Filter } from 'lucide-react';

const portfolioItems = [
  {
    id: 1,
    title: 'Premium LED Store Front',
    category: 'LED Sign Boards',
    image: 'https://images.unsplash.com/photo-1769710329350-167616dd56cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxpbGx1bWluYXRlZCUyMHN0b3JlJTIwc2lnbiUyMG5pZ2h0fGVufDB8fHx8MTc3MzkwNjY3M3ww&ixlib=rb-4.1.0&q=85',
    alt: 'LED sign board Ahmedabad',
  },
  {
    id: 2,
    title: 'Corporate Office Branding',
    category: 'Corporate Projects',
    image: 'https://images.unsplash.com/photo-1758448656987-cfae6bf225e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBvZmZpY2UlMjBsb2JieSUyMGJyYW5kaW5nJTIwbG9nbyUyMHdhbGx8ZW58MHx8fHwxNzczOTA2NjY0fDA&ixlib=rb-4.1.0&q=85',
    alt: 'Corporate signage Gujarat',
  },
  {
    id: 3,
    title: 'Modern Retail Storefront',
    category: 'Retail Branding',
    image: 'https://images.unsplash.com/photo-1770021601100-4b9092946653?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZXRhaWwlMjBzdG9yZWZyb250JTIwc2lnbmFnZSUyMG5pZ2h0fGVufDB8fHx8MTc3MzkwNjY2NXww&ixlib=rb-4.1.0&q=85',
    alt: 'Retail signage Ahmedabad',
  },
  {
    id: 4,
    title: 'Custom Neon Art Installation',
    category: 'Acrylic Signage',
    image: 'https://images.unsplash.com/photo-1763336323894-9a973c9779b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxuZW9uJTIwc2lnbiUyMGN1c3RvbSUyMGFydGlzdGljfGVufDB8fHx8MTc3MzkwNjY2Mnww&ixlib=rb-4.1.0&q=85',
    alt: 'Acrylic signage Gujarat',
  },
  {
    id: 5,
    title: 'Night Illuminated Signage',
    category: 'LED Sign Boards',
    image: 'https://images.unsplash.com/photo-1758445515620-e6039c1cd913?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxpbGx1bWluYXRlZCUyMHN0b3JlJTIwc2lnbiUyMG5pZ2h0fGVufDB8fHx8MTc3MzkwNjY3M3ww&ixlib=rb-4.1.0&q=85',
    alt: 'Glow sign board Ahmedabad',
  },
  {
    id: 6,
    title: 'Building Facade Signage',
    category: 'Corporate Projects',
    image: 'https://images.unsplash.com/photo-1726146347502-a2c49bc11685?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBidWlsZGluZyUyMHdpdGglMjBsZWQlMjBzaWduYWdlJTIwbmlnaHR8ZW58MHx8fHwxNzczOTA2NjYxfDA&ixlib=rb-4.1.0&q=85',
    alt: 'Building signage Gujarat',
  },
];

const categories = ['All', 'LED Sign Boards', 'Acrylic Signage', 'Retail Branding', 'Corporate Projects'];

const PortfolioItem = ({ item, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className="portfolio-item group rounded-2xl overflow-hidden shadow-soft"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      layout
      data-testid={`portfolio-item-${index}`}
    >
      <img
        src={item.image}
        alt={item.alt}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      <div className="portfolio-overlay">
        <div>
          <span className="text-xs text-sky-400 uppercase tracking-wider mb-1 block font-['Manrope']">
            {item.category}
          </span>
          <h4 className="text-lg font-['Oswald'] font-bold text-white">
            {item.title}
          </h4>
        </div>
        <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white/30">
          <ExternalLink className="w-4 h-4 text-white" />
        </button>
      </div>
    </motion.div>
  );
};

const PortfolioSection = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const filteredItems = activeCategory === 'All'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === activeCategory);

  return (
    <section
      id="portfolio"
      className="py-24 md:py-32 bg-white relative overflow-hidden"
      data-testid="portfolio-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          ref={ref}
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sky-500 text-sm font-semibold uppercase tracking-[0.2em] mb-4 block font-['Manrope']">
            Our Work
          </span>
          <h2 className="section-heading mb-6">
            PREMIUM <span className="gradient-text">PORTFOLIO</span>
          </h2>
          <p className="section-subheading mx-auto">
            Explore our collection of premium signage projects delivered across Ahmedabad and Gujarat.
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Filter className="w-4 h-4 text-slate-400 mr-2" />
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 text-sm font-['Manrope'] font-medium rounded-full transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-sky-500 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`filter-${category.toLowerCase().replace(/\s/g, '-')}`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Portfolio Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          layout
        >
          <AnimatePresence mode="popLayout">
            {filteredItems.map((item, index) => (
              <PortfolioItem key={item.id} item={item} index={index} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default PortfolioSection;
